// features/updates/updatesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../app/firebase';
import {
  collection, getDocs, query, orderBy, doc, getDoc, setDoc, serverTimestamp, Timestamp, deleteField,
} from 'firebase/firestore';

// ——— helpers ———
const tsToMillis = (ts) => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : null);
const normalizeUpdate = (id, data, extra = {}) => ({
  id,
  ...data,
  publishedAt: tsToMillis(data.publishedAt),
  createdAt: tsToMillis(data.createdAt),
  updatedAt: tsToMillis(data.updatedAt),
  webOnly: data.webOnly === true,
  mobileOnly: data.mobileOnly === true,
  ...extra,
});

// ——— thunks ———

// список обновлений (сортировка по версии или дате публикации)
export const fetchUpdates = createAsyncThunk('updates/fetchAll', async () => {
  const q = query(collection(db, 'updates'), orderBy('publishedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => normalizeUpdate(d.id, d.data()));
});

export const fetchUpdateById = createAsyncThunk('updates/fetchById', async (id) => {
  const ref = doc(db, 'updates', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Update not found');
  return normalizeUpdate(snap.id, snap.data());
});

// создание/обновление обновления (для админа)
export const saveUpdate = createAsyncThunk('updates/save', async ({ id, data }, { rejectWithValue }) => {
  try {
    const now = serverTimestamp();
    const {
      webOnly,
      mobileOnly,
      publishedAt: publishedAtInput,
      ...rest
    } = data;

    let publishedAt;
    if (publishedAtInput instanceof Date) {
      publishedAt = Timestamp.fromDate(publishedAtInput);
    } else if (publishedAtInput) {
      publishedAt = publishedAtInput;
    } else {
      publishedAt = now;
    }

    const updateData = {
      ...rest,
      publishedAt,
      webOnly: webOnly ? true : deleteField(),
      mobileOnly: mobileOnly ? true : deleteField(),
      added: deleteField(),
      changed: deleteField(),
      fixed: deleteField(),
      version: deleteField(),
      updatedAt: now,
      ...(id ? {} : { createdAt: now }),
    };

    const ref = id ? doc(db, 'updates', id) : doc(collection(db, 'updates'));
    await setDoc(ref, updateData, { merge: true });
    
    // После сохранения получаем документ обратно для нормализации
    const savedSnap = await getDoc(ref);
    return normalizeUpdate(ref.id, savedSnap.data());
  } catch (e) {
    return rejectWithValue(e.message || 'Failed to save update');
  }
});

// ——— state ———

const initialState = {
  items: [],           // список всех обновлений
  byId: {},           // кэш по id
  loadingList: false,
  loadingItem: false,
  saving: false,
  error: null,
};

// ——— slice ———

const updatesSlice = createSlice({
  name: 'updates',
  initialState,
  reducers: {
    resetUpdatesState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchUpdates
      .addCase(fetchUpdates.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchUpdates.fulfilled, (state, action) => {
        state.loadingList = false;
        state.items = action.payload;
        action.payload.forEach(u => {
          state.byId[u.id] = u;
        });
      })
      .addCase(fetchUpdates.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.error?.message || 'Failed to load updates';
      })

      // fetchUpdateById
      .addCase(fetchUpdateById.pending, (state) => {
        state.loadingItem = true;
        state.error = null;
      })
      .addCase(fetchUpdateById.fulfilled, (state, action) => {
        state.loadingItem = false;
        const u = action.payload;
        state.byId[u.id] = u;
      })
      .addCase(fetchUpdateById.rejected, (state, action) => {
        state.loadingItem = false;
        state.error = action.error?.message || 'Failed to load update';
      })

      // saveUpdate
      .addCase(saveUpdate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUpdate.fulfilled, (state, action) => {
        state.saving = false;
        const u = action.payload;
        state.byId[u.id] = u;
        // Обновляем список, если обновление уже там есть
        const index = state.items.findIndex(item => item.id === u.id);
        if (index >= 0) {
          state.items[index] = u;
        } else {
          // Если новое, добавляем в начало (так как сортировка по publishedAt desc)
          state.items.unshift(u);
          state.items.sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0));
        }
      })
      .addCase(saveUpdate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || 'Failed to save update';
      });
  },
});

// ——— selectors ———

export const { resetUpdatesState } = updatesSlice.actions;

export const selectUpdates = (state) => state.updates.items;
export const selectUpdatesLoading = (state) => state.updates.loadingList;
export const selectUpdateLoading = (state) => state.updates.loadingItem;
export const selectUpdateSaving = (state) => state.updates.saving;
export const selectUpdateError = (state) => state.updates.error;

export const selectUpdateById = (id) => (state) => state.updates.byId[id];

export default updatesSlice.reducer;

